import { useTranslations } from "@zapier/i18n";
import { useMemo } from "react";
import {
  BetaPageHeaderBase,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import {
  useFolderAncestors,
  FolderTreeItem,
  useCurrentUser,
  useMembers,
  useSupportedAssets,
} from "@zapier/client";
import {
  FOLDER_TRASH_ID,
  getCreateZapUrl,
  getVirtualFolderName,
  isVirtualFolder,
} from "@zapier/utils";
import {
  KebabMenu,
  useFolderAndZapsModalStates,
  useRouterParams,
} from "@zapier/shared";
import styles from "./FolderHeader.module.css";
import { FolderBreadcrumbs } from "../FolderBreadcrumbs";
import { FolderHeaderTitle } from "../FolderHeaderTitle";
import { FolderHeaderSharing } from "../FolderHeaderSharing";
import { CreationButton } from "../../shared/components/CreationButton/CreationButton";
import { useFolderActionItems } from "../../folders/useFolderActionItems";
import { FolderModals } from "../../folders/FolderTable/FolderModals";

interface FolderHeaderProps {
  folder?: FolderTreeItem;
  folderId: string;
  isFoldersLoading?: boolean;
}

const FolderHeader = ({
  folder,
  folderId,
  isFoldersLoading,
}: FolderHeaderProps) => {
  const t = useTranslations("folder-header");

  const { memberId } = useRouterParams();
  const { user, isLoading: isUserLoading } = useCurrentUser();

  const {
    currentFolder,
    setCurrentFolder,
    currentFolderModal,
    setCurrentFolderModal,
  } = useFolderAndZapsModalStates();

  const { ancestors, isAncestorsLoading } = useFolderAncestors({
    folderId,
  });

  const { isMPFEnabled } = useSupportedAssets();

  const { members, isLoading: isMembersLoading } = useMembers({
    ids: memberId ? [Number(memberId)] : [],
  });

  const folderName = useMemo(() => {
    if (folder?.name) return folder?.name;

    // Handle virtual folders with centralized logic
    if (folderId && isVirtualFolder(folderId)) {
      return getVirtualFolderName(folderId, {
        user,
        memberId,
        members,
      });
    }

    return "";
  }, [
    folder?.name,
    folderId,
    isUserLoading,
    isMembersLoading,
    memberId,
    members,
    user,
  ]);

  const icon = folderId === FOLDER_TRASH_ID ? "actionTrash" : undefined;

  const shouldShowSharing = folder?.isShared;

  const folderActionItems = useFolderActionItems({
    folder,
    folderLevel: ancestors?.length || 0,
    setCurrentFolder,
    setCurrentModal: setCurrentFolderModal,
  });

  // Use centralized logic for breadcrumb folder names
  const breadcrumbFolderName = useMemo(() => {
    if (folderId && isVirtualFolder(folderId)) {
      return getVirtualFolderName(folderId, {
        user,
        memberId,
        members,
      });
    }
    return folder?.name;
  }, [folder?.name, folderId, memberId, members, user]);

  return (
    <>
      <BetaPageHeaderBase
        topRowContent={
          <FolderBreadcrumbs
            ancestors={ancestors}
            currentFolderName={breadcrumbFolderName}
            isAncestorsLoading={isAncestorsLoading}
            hasAssetsRoot={isMPFEnabled}
            user={user}
            memberId={memberId}
            members={members}
          />
        }
      >
        <div className={styles.folderHeader}>
          <FolderHeaderTitle
            icon={icon}
            title={folderName}
            isFoldersLoading={
              isFoldersLoading || isUserLoading || isMembersLoading
            }
            isShared={folder?.isShared}
          />

          {folderId !== FOLDER_TRASH_ID && (
            <div className={styles.folderHeaderInfo}>
              {shouldShowSharing && <FolderHeaderSharing />}

              <CreationButton
                createZapHref={getCreateZapUrl(folderId)}
                parentId={folder?.id ?? folderId}
                parentName={folder?.name}
                parentIsShared={folder?.isShared}
              />

              <Tooltip
                allowMultiline={true}
                content={t("tooltips.actions")}
                key="kebab-folderHeader"
                placement="bottom"
              >
                <TooltipFocusableTrigger>
                  <KebabMenu
                    menuLabel={t("menu.folder-options-label")}
                    buttonLabel={t("menu.toggle-folder-options-aria-label")}
                    menuItems={folderActionItems}
                    size="medium"
                  />
                </TooltipFocusableTrigger>
              </Tooltip>
            </div>
          )}
        </div>
      </BetaPageHeaderBase>
      <FolderModals
        currentModal={currentFolderModal}
        setCurrentModal={setCurrentFolderModal}
        currentFolder={currentFolder}
        setCurrentFolder={setCurrentFolder}
      />
    </>
  );
};

export default FolderHeader;
